'use client';
import React, { useState, useEffect } from 'react';
import AppLayout from '@/components/AppLayout';
import { Building2, Plus, Trash2, ChevronDown, ChevronUp, BarChart3, Shield, Globe, Cpu, AlertTriangle, Loader2 } from 'lucide-react';
import { getCompanyAnalyses, saveCompanyAnalysis, deleteCompanyAnalysis, CompanyAnalysis } from '@/lib/storage';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';


function generateAnalysis(
  companyName: string,
  suppliers: string[],
  regions: string[],
  logisticsRoutes: string[],
  dependencies: string[]
): Omit<CompanyAnalysis, 'id' | 'createdAt' | 'updatedAt'> {
  // Deterministic scoring based on inputs
  const regionRisk: Record<string, number> = {
    'china': 65, 'russia': 80, 'iran': 90, 'taiwan': 70, 'ukraine': 75,
    'middle east': 72, 'africa': 55, 'europe': 25, 'us': 15, 'usa': 15,
    'asia': 50, 'india': 35, 'brazil': 40, 'latam': 45,
  };

  let geoScore = 20;
  regions.forEach(r => {
    const key = r.toLowerCase();
    for (const [region, score] of Object.entries(regionRisk)) {
      if (key.includes(region)) geoScore = Math.max(geoScore, score);
    }
  });

  const supplierRisk = Math.min(30 + suppliers.length * 5, 70);
  const logisticsRisk = Math.min(20 + logisticsRoutes.length * 8, 65);
  const depRisk = Math.min(15 + dependencies.length * 6, 60);

  const operationalScore = Math.round((supplierRisk + logisticsRisk) / 2);
  const geopoliticalScore = Math.round(geoScore);
  const sanctionsScore = Math.round(geoScore * 0.8 + depRisk * 0.2);
  const cyberScore = Math.round(30 + dependencies.length * 4 + (regions.length > 3 ? 15 : 0));

  const avg = (operationalScore + geopoliticalScore + sanctionsScore + cyberScore) / 4;
  const overallRisk: CompanyAnalysis['overallRisk'] =
    avg >= 70 ? 'Critical' : avg >= 50 ? 'High' : avg >= 30 ? 'Medium' : 'Low';

  const summary = `${companyName} presents a ${overallRisk.toLowerCase()} overall risk profile based on ${regions.length} monitored region(s) and ${suppliers.length} supplier(s). ${
    geopoliticalScore >= 60 ? 'Elevated geopolitical exposure detected — recommend immediate review of regional dependencies. ' : ''
  }${sanctionsScore >= 60 ? 'Sanctions screening recommended for key counterparties. ' : ''
  }${cyberScore >= 50 ? 'Cyber risk elevated due to digital dependency footprint. ' : ''
  }Operational resilience score reflects ${suppliers.length > 3 ? 'a complex' : 'a manageable'} supply chain structure.`;

  return {
    companyName,
    suppliers,
    regions,
    logisticsRoutes,
    dependencies,
    operationalScore,
    geopoliticalScore,
    sanctionsScore,
    cyberScore,
    overallRisk,
    summary,
  };
}

const riskColor = (score: number) =>
  score >= 70 ? 'text-danger' : score >= 50 ? 'text-warning' : score >= 30 ? 'text-info' : 'text-success';

const riskBg = (score: number) =>
  score >= 70 ? 'bg-danger' : score >= 50 ? 'bg-warning' : score >= 30 ? 'bg-info' : 'bg-success';

const overallBadge: Record<CompanyAnalysis['overallRisk'], string> = {
  Critical: 'bg-danger/10 text-danger border-danger/20',
  High: 'bg-warning/10 text-warning border-warning/20',
  Medium: 'bg-info/10 text-info border-info/20',
  Low: 'bg-success/10 text-success border-success/20',
};

export default function CompanyAnalyserPage() {
  const [analyses, setAnalyses] = useState<CompanyAnalysis[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [isAnalysing, setIsAnalysing] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const [form, setForm] = useState({
    companyName: '',
    suppliersRaw: '',
    regionsRaw: '',
    routesRaw: '',
    depsRaw: '',
  });

  useEffect(() => {
    setAnalyses(getCompanyAnalyses());
  }, []);

  const splitList = (raw: string) =>
    raw.split(',').map(s => s.trim()).filter(Boolean);

  const handleAnalyse = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.companyName.trim()) return;
    setIsAnalysing(true);
    await new Promise(r => setTimeout(r, 1500));

    const result = generateAnalysis(
      form.companyName,
      splitList(form.suppliersRaw),
      splitList(form.regionsRaw),
      splitList(form.routesRaw),
      splitList(form.depsRaw),
    );

    const now = new Date().toISOString();
    const analysis: CompanyAnalysis = {
      id: `ca-${Date.now()}`,
      ...result,
      createdAt: now,
      updatedAt: now,
    };

    saveCompanyAnalysis(analysis);
    setAnalyses(getCompanyAnalyses());
    setIsAnalysing(false);
    setShowForm(false);
    setExpandedId(analysis.id);
    setForm({ companyName: '', suppliersRaw: '', regionsRaw: '', routesRaw: '', depsRaw: '' });
    toast.success(`Analysis complete for ${form.companyName}`, { description: `Overall risk: ${analysis.overallRisk}` });
  };

  const handleDelete = (id: string) => {
    deleteCompanyAnalysis(id);
    setAnalyses(getCompanyAnalyses());
    if (expandedId === id) setExpandedId(null);
    toast.success('Analysis deleted');
  };

  return (
    <AppLayout activeRoute="/company-analyser">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Company Risk Analyser</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Generate AI-powered risk scores for companies, suppliers, and operational exposure
            </p>
          </div>
          <button
            onClick={() => setShowForm(v => !v)}
            className="flex items-center gap-2 bg-primary text-primary-foreground text-sm font-semibold px-4 py-2 rounded-md hover:opacity-90 active:scale-95 transition-all"
          >
            <Plus size={14} />
            Analyse Company
          </button>
        </div>

        {/* Analysis Form */}
        {showForm && (
          <div className="card-elevated rounded-xl border border-border p-6 animate-fade-in">
            <h2 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
              <Building2 size={15} className="text-primary" />
              New Company Risk Assessment
            </h2>
            <form onSubmit={handleAnalyse} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-foreground mb-1.5">Company Name *</label>
                <input
                  type="text"
                  value={form.companyName}
                  onChange={e => setForm(f => ({ ...f, companyName: e.target.value }))}
                  placeholder="e.g. Acme Corporation"
                  className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                  required
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-foreground mb-1.5">Suppliers <span className="text-muted-foreground">(comma-separated)</span></label>
                  <textarea
                    value={form.suppliersRaw}
                    onChange={e => setForm(f => ({ ...f, suppliersRaw: e.target.value }))}
                    placeholder="e.g. TSMC, Samsung, Foxconn"
                    rows={2}
                    className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring resize-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground mb-1.5">Operating Regions <span className="text-muted-foreground">(comma-separated)</span></label>
                  <textarea
                    value={form.regionsRaw}
                    onChange={e => setForm(f => ({ ...f, regionsRaw: e.target.value }))}
                    placeholder="e.g. China, Middle East, Europe"
                    rows={2}
                    className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring resize-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground mb-1.5">Logistics Routes <span className="text-muted-foreground">(comma-separated)</span></label>
                  <textarea
                    value={form.routesRaw}
                    onChange={e => setForm(f => ({ ...f, routesRaw: e.target.value }))}
                    placeholder="e.g. Red Sea, Suez Canal, Trans-Siberian"
                    rows={2}
                    className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring resize-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground mb-1.5">Strategic Dependencies <span className="text-muted-foreground">(comma-separated)</span></label>
                  <textarea
                    value={form.depsRaw}
                    onChange={e => setForm(f => ({ ...f, depsRaw: e.target.value }))}
                    placeholder="e.g. Rare earth metals, Cloud infrastructure, SWIFT"
                    rows={2}
                    className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring resize-none"
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="border border-border rounded-md px-4 py-2 text-sm text-muted-foreground hover:bg-muted transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isAnalysing}
                  className="flex items-center gap-2 bg-primary text-primary-foreground rounded-md px-6 py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-60 transition-all"
                >
                  {isAnalysing ? (
                    <><Loader2 size={14} className="animate-spin" /> Analysing…</>
                  ) : (
                    <><BarChart3 size={14} /> Run Analysis</>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Results */}
        {analyses.length === 0 ? (
          <div className="card-elevated rounded-xl border border-border p-12 text-center">
            <Building2 size={32} className="text-muted-foreground mx-auto mb-3" />
            <p className="text-sm font-medium text-foreground mb-1">No analyses yet</p>
            <p className="text-xs text-muted-foreground">Click &quot;Analyse Company&quot; to generate your first risk assessment.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {analyses.map((analysis) => (
              <div key={analysis.id} className="card-elevated rounded-xl border border-border overflow-hidden">
                {/* Card header */}
                <div
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/20 transition-colors"
                  onClick={() => setExpandedId(expandedId === analysis.id ? null : analysis.id)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Building2 size={16} className="text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">{analysis.companyName}</p>
                      <p className="text-xs text-muted-foreground">
                        {analysis.regions.length} region(s) · {analysis.suppliers.length} supplier(s) · {new Date(analysis.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-md border ${overallBadge[analysis.overallRisk]}`}>
                      {analysis.overallRisk} Risk
                    </span>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(analysis.id); }}
                      className="p-1.5 rounded hover:bg-danger/10 text-muted-foreground hover:text-danger transition-colors"
                      title="Delete analysis"
                    >
                      <Trash2 size={13} />
                    </button>
                    {expandedId === analysis.id ? <ChevronUp size={15} className="text-muted-foreground" /> : <ChevronDown size={15} className="text-muted-foreground" />}
                  </div>
                </div>

                {/* Expanded detail */}
                {expandedId === analysis.id && (
                  <div className="border-t border-border p-4 space-y-5 animate-fade-in">
                    {/* Score grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {[
                        { label: 'Operational Exposure', score: analysis.operationalScore, icon: BarChart3 },
                        { label: 'Geopolitical Vulnerability', score: analysis.geopoliticalScore, icon: Globe },
                        { label: 'Sanctions Exposure', score: analysis.sanctionsScore, icon: Shield },
                        { label: 'Cyber Risk', score: analysis.cyberScore, icon: Cpu },
                      ].map(({ label, score, icon: Icon }) => (
                        <div key={label} className="bg-muted/30 rounded-lg p-3">
                          <div className="flex items-center gap-1.5 mb-2">
                            <Icon size={12} className="text-muted-foreground" />
                            <p className="text-2xs text-muted-foreground font-medium">{label}</p>
                          </div>
                          <p className={`text-2xl font-bold font-tabular ${riskColor(score)}`}>{score}</p>
                          <div className="mt-2 w-full bg-muted rounded-full h-1.5">
                            <div
                              className={`h-1.5 rounded-full ${riskBg(score)}`}
                              style={{ width: `${score}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Summary */}
                    <div className="bg-muted/20 rounded-lg p-4">
                      <p className="text-xs font-semibold text-foreground mb-2 flex items-center gap-1.5">
                        <AlertTriangle size={12} className="text-warning" />
                        AI Risk Summary
                      </p>
                      <p className="text-xs text-muted-foreground leading-relaxed">{analysis.summary}</p>
                    </div>

                    {/* Tags */}
                    <div className="grid grid-cols-2 gap-4 text-xs">
                      {analysis.suppliers.length > 0 && (
                        <div>
                          <p className="text-muted-foreground font-medium mb-1.5">Suppliers</p>
                          <div className="flex flex-wrap gap-1">
                            {analysis.suppliers.map(s => (
                              <span key={s} className="bg-muted px-2 py-0.5 rounded text-foreground">{s}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      {analysis.regions.length > 0 && (
                        <div>
                          <p className="text-muted-foreground font-medium mb-1.5">Regions</p>
                          <div className="flex flex-wrap gap-1">
                            {analysis.regions.map(r => (
                              <span key={r} className="bg-muted px-2 py-0.5 rounded text-foreground">{r}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      {analysis.logisticsRoutes.length > 0 && (
                        <div>
                          <p className="text-muted-foreground font-medium mb-1.5">Logistics Routes</p>
                          <div className="flex flex-wrap gap-1">
                            {analysis.logisticsRoutes.map(r => (
                              <span key={r} className="bg-muted px-2 py-0.5 rounded text-foreground">{r}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      {analysis.dependencies.length > 0 && (
                        <div>
                          <p className="text-muted-foreground font-medium mb-1.5">Dependencies</p>
                          <div className="flex flex-wrap gap-1">
                            {analysis.dependencies.map(d => (
                              <span key={d} className="bg-muted px-2 py-0.5 rounded text-foreground">{d}</span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
